import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t, tKey } from 'ember-intl';
import {
  PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS,
  type PortfolioSmartInsightsSortOption,
} from 'lumen/utils/constants/portfolio-smart-insights-constants';

const PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS_TRANSLATIONS = {
  [PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS.PRIORITY]: tKey(
    'market.smart_insights.list_sort_options.priority',
  ),
  [PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS.STAY_DATE]: tKey(
    'market.smart_insights.list_sort_options.stay_date',
  ),
} as const;

interface MarketSmartInsightsDropdownSortSignature {
  Args: {
    setSortBy: (sortOption: PortfolioSmartInsightsSortOption) => void;
    sortBy: PortfolioSmartInsightsSortOption;
  };
  Element: HTMLDivElement;
}

type DropdownSortOption = Option & {
  label: string;
  value: PortfolioSmartInsightsSortOption;
};

export default class MarketSmartInsightsDropdownSort extends Component<MarketSmartInsightsDropdownSortSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownSortOption[] {
    return Object.values(PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS).map(
      (option) => ({
        label: this.intl.t(
          PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS_TRANSLATIONS[option],
        ),
        value: option,
      }),
    );
  }

  get selectedOption(): DropdownSortOption | undefined {
    const { sortBy } = this.args;

    return this.options.find((option) => option.value === sortBy);
  }

  @action onChangeSort(selectedOption: DropdownSortOption): void {
    this.args.setSortBy(selectedOption.value);

    this.usageAnalytics.track('Smart Insights Sorted', {
      eventTrigger: 'smart-insights-dropdown-sorting',
      product: 'smart-insights-multi-property',
      value: selectedOption.value,
    });
  }

  <template>
    <div class="flex flex-row items-center gap-100">
      <span class="text-300--regular">{{t "general.sort_by"}}</span>

      <PrismSelectInput
        @isSmall={{true}}
        @onChange={{this.onChangeSort}}
        @options={{this.options}}
        @value={{this.selectedOption}}
      />
    </div>
  </template>
}
